import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchFestivalMuhurta } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { Sunrise, Sunset, Star, Clock, Sparkles, AlertTriangle, RotateCcw, ArrowLeft, MapPin } from "lucide-react";

const prettyDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};

function WindowRow({ name, window, note, tone = "neutral", testid }) {
  const dot = tone === "good" ? "bg-green-600" : tone === "bad" ? "bg-red-500" : "bg-gold";
  return (
    <div data-testid={testid} className="flex items-start justify-between gap-4 border-b border-line py-3 last:border-0">
      <div className="min-w-0">
        <p className="flex items-center gap-2 font-serif-latin text-ink"><span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />{name}</p>
        {note && <p className="mt-0.5 pl-4 text-sm leading-relaxed text-ink-3">{note}</p>}
      </div>
      <p className="shrink-0 whitespace-nowrap font-serif-latin text-ink-2">{window || "—"}</p>
    </div>
  );
}

export default function CityFestivalMuhurta() {
  const { festival: fslug, city: cslug } = useParams();
  const { pick } = useLang();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    fetchFestivalMuhurta(fslug, cslug)
      .then((d) => { if (active) { setData(d); setState("ok"); } })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [fslug, cslug]);

  if (state === "loading") {
    return <div data-testid="muhurta-loading" className="mx-auto max-w-5xl px-5 py-24 text-center text-ink-3">{pick("मुहूर्त गणना हो रही है…", "Computing muhurta…")}</div>;
  }
  if (state === "error" || !data) {
    return (
      <div data-testid="muhurta-error" className="mx-auto max-w-5xl px-5 py-24 text-center">
        <p className="text-ink-2">{pick("यह मुहूर्त पृष्ठ उपलब्ध नहीं है।", "This festival muhurta page is not available.")}</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 rounded-full border border-saffron px-5 py-2.5 text-sm font-medium text-saffron hover:bg-saffron hover:text-white"><RotateCcw className="h-4 w-4" /> {pick("पुनः प्रयास", "Retry")}</button>
          <Link to="/muhurta" className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm text-ink-2 hover:text-ink"><ArrowLeft className="h-4 w-4" /> {pick("सभी मुहूर्त", "All muhurtas")}</Link>
        </div>
      </div>
    );
  }

  const { festival: F, city: C, panchang: P, muhurtas, siddhi_yogas, varjyam, auspicious, inauspicious } = data;
  const name = pick(F.name_hi, F.name) || F.name;
  const place = `${C.name}${C.country ? ", " + C.country : ""}`;
  const dateStr = prettyDate(data.date_iso);
  const title = `${F.name} Muhurta in ${C.name} — ${dateStr} Timings`;
  const description = `${F.name} ${dateStr} in ${place}: puja muhurta, Rahu Kalam, Abhijit & auspicious timings computed in ${C.name}'s local time. ${F.significance}`;

  return (
    <div data-testid="city-festival-muhurta-page">
      <Seo
        path={`/muhurta/${fslug}/${cslug}`}
        title={title}
        description={description}
        keywords={`${F.name} muhurta ${C.name}, ${F.name} puja time ${C.name}, ${F.name} ${C.name} timings, ${C.name} ${F.name} ${data.date_iso}`}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Event",
          name: `${F.name} — ${C.name}`, startDate: data.date_iso,
          description, eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          location: { "@type": "Place", name: place },
        }}
      />
      <PageHero
        art={HERO_ART.devotion}
        overline={pick("नगर मुहूर्त", "City Muhurta")}
        deva={F.name_hi || "मुहूर्त"}
        en={`${name} · ${C.name}`}
        tagline={`${dateStr} · ${place}`}
      />

      <section className="mx-auto max-w-3xl px-5 py-10 md:px-8">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink-3">
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-saffron" /> {place}</span>
          <span className="inline-flex items-center gap-1.5"><Sunrise className="h-4 w-4 text-gold" /> {pick("सूर्योदय", "Sunrise")} {P.sunrise || "—"}</span>
          <span className="inline-flex items-center gap-1.5"><Sunset className="h-4 w-4 text-saffron" /> {pick("सूर्यास्त", "Sunset")} {P.sunset || "—"}</span>
        </div>
        {F.significance && <p className="mt-5 border-l-2 border-gold/50 pl-4 text-base leading-relaxed text-ink-2">{pick(F.significance_hi, F.significance)}</p>}

        {/* Day panchang */}
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="muhurta-panchang">
          {[[pick("तिथि", "Tithi"), P.tithi], [pick("नक्षत्र", "Nakshatra"), P.nakshatra], [pick("योग", "Yoga"), P.yoga], [pick("वार", "Vara"), P.vara]].map(([l, v]) => (
            <div key={l} className="border border-line bg-surface p-4 text-center">
              <p className="text-xs uppercase tracking-wide text-ink-3">{l}</p>
              <p className="mt-1 font-serif-latin text-lg text-ink">{v || "—"}</p>
            </div>
          ))}
        </div>

        {/* Festival puja muhurtas */}
        <h2 className="mt-12 flex items-center gap-2 font-serif-latin text-2xl text-ink"><Clock className="h-5 w-5 text-saffron" /> {pick("पूजा मुहूर्त", "Puja Muhurtas")}</h2>
        <div className="mt-4 border border-line bg-surface p-5" data-testid="muhurta-pujas">
          {muhurtas.length ? muhurtas.map((m, i) => (
            <WindowRow key={i} testid={`puja-muhurta-${i}`} name={m.name} window={m.window} tone="good"
              note={[m.significance, m.authority].filter(Boolean).join(" · ")} />
          )) : <p className="text-sm text-ink-3" data-testid="muhurta-pujas-empty">{pick("इस दिन इस नगर हेतु कोई विशिष्ट पूजा मुहूर्त उपलब्ध नहीं।", "No specific puja muhurta is listed for this city on this day.")}</p>}
        </div>

        {/* Siddhi yogas */}
        {siddhi_yogas.length > 0 && (
          <>
            <h2 className="mt-10 flex items-center gap-2 font-serif-latin text-2xl text-ink"><Sparkles className="h-5 w-5 text-gold" /> {pick("सिद्धि योग", "Siddhi Yogas")}</h2>
            <div className="mt-4 border border-line bg-surface p-5" data-testid="muhurta-siddhi">
              {siddhi_yogas.map((s, i) => <WindowRow key={i} testid={`siddhi-${i}`} name={s.name} window={s.window} note={s.significance} tone="good" />)}
            </div>
          </>
        )}

        {/* Auspicious windows */}
        <h2 className="mt-10 flex items-center gap-2 font-serif-latin text-2xl text-ink"><Star className="h-5 w-5 text-green-600" /> {pick("शुभ काल", "Auspicious Windows")}</h2>
        <div className="mt-4 border border-line bg-surface p-5" data-testid="muhurta-auspicious">
          {auspicious.length ? auspicious.map((w, i) => <WindowRow key={i} testid={`ausp-${i}`} name={w.name} window={w.window} tone="good" />)
            : <p className="text-sm text-ink-3">{pick("उपलब्ध नहीं।", "Not available.")}</p>}
        </div>

        {/* Inauspicious windows */}
        <h2 className="mt-10 flex items-center gap-2 font-serif-latin text-2xl text-ink"><AlertTriangle className="h-5 w-5 text-red-500" /> {pick("अशुभ काल", "Times to Avoid")}</h2>
        <div className="mt-4 border border-line bg-surface p-5" data-testid="muhurta-inauspicious">
          {inauspicious.map((w, i) => <WindowRow key={i} testid={`inausp-${i}`} name={w.name} window={w.window} tone="bad" />)}
          {varjyam.map((w, i) => <WindowRow key={`v${i}`} testid={`varjyam-${i}`} name={w.name} window={w.window} tone="bad" />)}
        </div>

        <p className="mt-8 text-xs leading-relaxed text-ink-3">
          {pick(
            `सभी समय ${C.name} के स्थानीय समयानुसार, बाह्य ज्योतिष इंजन से लाइव गणना किए गए हैं।`,
            `All times are computed live in ${C.name}'s local time from the Jyotish engine. Observe local sampradaya and your family/guru tradition.`)}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={`/muhurta/${fslug}`} className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("अन्य नगरों में " + F.name, F.name + " in other cities")}</Link>
          <Link to={`/panchangam/${cslug}`} className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{C.name} {pick("पंचांगम्", "Panchangam")}</Link>
          <Link to="/muhurta" className="border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("सभी त्योहार मुहूर्त", "All festival muhurtas")}</Link>
        </div>
      </section>
    </div>
  );
}
